import {
  Alignment,
  ConversationBubble,
  Direction,
  Displaying,
  Spacing,
  Typography,
} from "@bonusx-global/bx-design-system";
import styled from "@emotion/styled";
import type { IChatbotMessage } from "../../entities/chatbot-message.entity";
import type { FeedbackEnum } from "../../enums/feedback.enum";
import type { NegativeFeedbackReasonEnum } from "../../enums/negative-feedback-reason.enum";
import { SenderTypeEnum } from "../../enums/sender-type.enum";
import { AiChatbotIconContainer } from "../ai-chatbot-icon-container/ai-chatbot-icon-container";
import { AiChatbotIcon } from "../ai-chatbot-icon/ai-chatbot-icon";
import AiResponseFeedback from "./ai-response-feedback/ai-response-feedback";
import AiResponseMarkdownMessageParser from "./ai-response-markdown-message-parser/ai-response-markdown-message-parser";

const AiMessageContainer = styled("div")({
  display: Displaying.flex,
  flexDirection: Direction.column,
  gap: Spacing["750"],
  alignSelf: Alignment.flexStart,
  maxWidth: "100%",
  wordBreak: "break-word",
});

const UserMessageContainer = styled(ConversationBubble)({
  alignSelf: Alignment.flexEnd,
});

interface ChatbotMessageItemProps {
  message: IChatbotMessage;
  isStreamingResponse?: boolean;
  onFeedback?: (messageId: number, feedback: FeedbackEnum | null) => Promise<void>;
  onNegativeFeedbackReason?: (messageId: number, reason: NegativeFeedbackReasonEnum) => Promise<void>;
  animateAiMessage?: boolean;
}

export function ChatbotMessageItem({
  message,
  isStreamingResponse,
  onFeedback,
  onNegativeFeedbackReason,
  animateAiMessage = false,
}: Readonly<ChatbotMessageItemProps>) {
  const shouldShowFeedback = onFeedback !== undefined && isStreamingResponse === false;

  function handleFeedback(feedback: FeedbackEnum | null) {
    onFeedback?.(message.id as number, feedback);
  }

  function handleNegativeFeedbackReason(reason: NegativeFeedbackReasonEnum) {
    onNegativeFeedbackReason?.(message.id as number, reason);
  }

  return message.senderType === SenderTypeEnum.USER ? (
    <UserMessageContainer text={message.text} fullWidth={false} maxWidth="100%" />
  ) : (
    <AiMessageContainer>
      <AiChatbotIconContainer>
        <AiChatbotIcon animated={false} />
      </AiChatbotIconContainer>
      <Typography variant="body1" color="textPrimary" component="div">
        <AiResponseMarkdownMessageParser markdownString={message.text} animate={animateAiMessage} />
      </Typography>
      {shouldShowFeedback && (
        <AiResponseFeedback
          feedback={message.feedback}
          negativeFeedbackReason={message.negativeFeedbackReason}
          onFeedback={handleFeedback}
          onNegativeFeedbackReason={handleNegativeFeedbackReason}
        />
      )}
    </AiMessageContainer>
  );
}
